/**
 * Zone display status derived from LIVE capacity utilisation.
 *
 * The static `zone.status` persisted in the zone catalogue is never updated by
 * the runtime, so any screen that shows a status pill / border colour must
 * derive it from the current utilisation instead. Pure module (no React, no
 * alias imports) so it can be unit-tested in isolation.
 *
 * Thresholds mirror the crowd density engine: warning >= 70, alert >= 85.
 */

export type ZoneDisplayStatus = 'alert' | 'warning' | 'normal' | 'nodata';

export const ZONE_WARNING_THRESHOLD = 70;
export const ZONE_ALERT_THRESHOLD = 85;

/** Minimal palette shape needed by the colour helper (structurally satisfied by ColorPalette). */
export interface ZoneStatusPalette {
  ember: string;
  amber: string;
  green: string;
  muted: string;
}

/** Status for a utilisation percentage; `nodata` when there is no numeric value. */
export function zoneStatusFromUtil(util: number | null | undefined): ZoneDisplayStatus {
  if (util === null || util === undefined) return 'nodata';
  const value = Number(util);
  if (!Number.isFinite(value)) return 'nodata';
  if (value >= ZONE_ALERT_THRESHOLD) return 'alert';
  if (value >= ZONE_WARNING_THRESHOLD) return 'warning';
  return 'normal';
}

/** Alias kept for callers that use the density vocabulary. */
export const densityStatusFromUtil = zoneStatusFromUtil;

export interface ZoneStatusSource {
  status?: 'normal' | 'warning' | 'alert' | null;
  capacityUtilisation?: number | null;
}

/**
 * Resolve the status to display: the projection's own status when present,
 * otherwise derived from the utilisation, otherwise `nodata`. The static
 * catalogue status is deliberately NOT a fallback.
 */
export function resolveZoneStatus(
  metric: ZoneStatusSource | null | undefined,
  snap?: { capacityUtilisation?: number | null } | null,
): ZoneDisplayStatus {
  if (metric?.status === 'alert' || metric?.status === 'warning' || metric?.status === 'normal') {
    return metric.status;
  }
  const util = metric?.capacityUtilisation ?? snap?.capacityUtilisation;
  return zoneStatusFromUtil(util ?? null);
}

export function zoneStatusLabel(status: ZoneDisplayStatus): string {
  if (status === 'alert') return 'Alert';
  if (status === 'warning') return 'Warning';
  if (status === 'normal') return 'Normal';
  return 'No data';
}

export function zoneStatusColor(status: ZoneDisplayStatus, C: ZoneStatusPalette): string {
  if (status === 'alert') return C.ember;
  if (status === 'warning') return C.amber;
  if (status === 'normal') return C.green;
  return C.muted;
}

/** Colour for a load percentage - same thresholds and tokens as the zone status colours above. */
export function loadColor(util: number | null | undefined, C: ZoneStatusPalette): string {
  return zoneStatusColor(zoneStatusFromUtil(util), C);
}

/** Colour for a density trend: rising is the one to watch, falling is relief, stable is quiet. */
export function trendColor(direction: string | null | undefined, C: ZoneStatusPalette): string {
  if (direction === 'rising') return C.ember;
  if (direction === 'falling') return C.green;
  return C.muted;
}

/**
 * The one colour for "active alerts" everywhere on the Crowd screens (map tags, zone panel, top bar), so the same
 * count never reads amber in one place and red in another.
 */
export function alertCountColor(C: { critical: string }): string {
  return C.critical;
}
